import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { tokens } from '@cellula/react-theme-patient';
import { Shimmer } from './new_shimmer.component';
import { type QRCodeToDataURLOptions } from 'qrcode';
import { Button, Flex, Icon, Text } from '@cellula/react';
import { withTranslation } from '@application/i18n/with_translation';

const QR_CODE_OPTIONS = {
	width: 200,
	margin: 0,
	errorCorrectionLevel: 'L',
} satisfies QRCodeToDataURLOptions;

interface Props {
	qrCode: string | null;
}

const StyledQrCodeWrapper = styled.div`
	width: 200px;
	height: 200px;
	box-sizing: content-box;
	border-radius: ${tokens.borderRadius.large};
	background-color: #fff;
`;

let qrCodeModule: Nullable<typeof import('qrcode')> = null;
let qrCodeModulePromise: Nullable<Promise<typeof import('qrcode')>> = null;

async function getUrlB64Image(str: string): Promise<string> {
	if (qrCodeModule == null) {
		qrCodeModulePromise = qrCodeModulePromise ?? import('qrcode');
		qrCodeModule = await qrCodeModulePromise;
		qrCodeModulePromise = null;
	}

	return qrCodeModule.toDataURL(str, QR_CODE_OPTIONS);
}

export function QrCode({ qrCode }: Props) {
	const [qrImage, setQrImage] = useState<string>();
	const [generationFailed, setGenerationFailed] = useState(false);

	function regenerateQrCode(qrCode: string) {
		setGenerationFailed(false);
		setQrImage(undefined);

		getUrlB64Image(qrCode)
			.then((url) => {
				setQrImage(url);
			})
			.catch(() => {
				setQrImage(undefined);
				setGenerationFailed(true);
			});
	}

	useEffect(() => {
		if (qrCode == null) return;

		const ac = new AbortController();

		getUrlB64Image(qrCode)
			.then((url) => {
				if (ac.signal.aborted) return;

				setQrImage(url);
			})
			.catch(() => {
				setQrImage(undefined);
				setGenerationFailed(true);
			});

		return () => {
			ac.abort();
		};
	}, [qrCode]);

	const code =
		qrImage != null ? (
			<img
				height={200}
				width={200}
				src={qrImage}
				alt={withTranslation('Component.QRCode.AltText')}
				style={{
					pointerEvents: 'none',
				}}
			/>
		) : (
			<QRSkeleton />
		);
	const failed = generationFailed ? (
		<Flex
			align='center'
			justify='center'
			gap={tokens.spacing.x1_5}
			style={{
				height: '100%',
				width: '100%',
				color: tokens.color.danger.content,
				boxSizing: 'border-box',
				textAlign: 'center',
			}}
		>
			<Icon type='warning' size='large' />
			<Text variant='labelSmallRegular'>{withTranslation('Component.QRCode.GenericError')}</Text>
			<Button
				variant='danger'
				size='xSmall'
				onClick={() => {
					if (qrCode == null) return;

					regenerateQrCode(qrCode);
				}}
			>
				{withTranslation('Component.QRCode.Retry')}
			</Button>
		</Flex>
	) : null;

	return <StyledQrCodeWrapper>{failed != null ? failed : code}</StyledQrCodeWrapper>;
}

function QRSkeleton() {
	return (
		<div
			style={{
				padding: '16px',
				height: '100%',
				width: '100%',
				position: 'relative',
			}}
		>
			<Shimmer cornerRadius={8} width='100%' height='100%' />
		</div>
	);
}
